import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  FormGroup,
  FormHelperText,
  HelperText,
  HelperTextItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextInput,
} from '@patternfly/react-core';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { formatMonthYear, formatWallClockDateTime } from '../../../utils/dateFormat';
import type { SplaReport } from '../../../types/models/Spla';
import type { ApiResponseSplaGenerate } from '../../../types/api/ApiResponseSpla';

const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

interface SplaGenerateReportModalProps {
  isOpen: boolean;
  /** The loaded month list — used to proactively refuse frozen months and to label Regenerate. */
  reports: SplaReport[];
  onClose: () => void;
  /** Hand back the refreshed report so the tab refetches (and reloads an open detail view). */
  onGenerated: (report: SplaReport) => void;
}

/**
 * Generate (or regenerate) one month's usage snapshot — the ONLY write on the usage surface. A
 * snapshot always reads CURRENT consumption, so generating a past month late stamps today's counts
 * on it (the warning below). The API refuses frozen and future months with a field error on
 * `month`; frozen months known from the loaded list are refused proactively before the call.
 */
function SplaGenerateReportModal({ isOpen, reports, onClose, onGenerated }: SplaGenerateReportModalProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();

  const [month, setMonth] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const trimmed = month.trim();
  const isFormatValid = MONTH_RE.test(trimmed);

  // Match the picked month against the loaded registry: an existing OPEN month makes this a
  // "Regenerate" (mention its current generation); a FROZEN month is refused before the call.
  const existing = useMemo(
    () => (isFormatValid ? (reports.find((r) => r.month === `${trimmed}-01`) ?? null) : null),
    [reports, trimmed, isFormatValid],
  );
  const frozenReason =
    existing !== null && existing.frozen
      ? `${formatMonthYear(existing.month)} is frozen — it was sent to the distributor on ${formatWallClockDateTime(existing.sent_at)} and can never be regenerated.`
      : null;

  const helperError =
    fieldError ?? frozenReason ?? (trimmed !== '' && !isFormatValid ? 'Enter a month as YYYY-MM.' : null);

  const canSubmit = !isGenerating && isFormatValid && frozenReason === null;

  const isRegenerate = existing !== null && !existing.frozen;

  const handleSubmit = async () => {
    setIsGenerating(true);
    setFieldError(null);
    try {
      const response = await apiFetch('/admin/spla/usage/generate', {
        method: 'POST',
        body: JSON.stringify({ month: trimmed }),
      });
      // Branch on the JSend body.status, NEVER response.ok.
      const body = (await response.json().catch(() => null)) as ApiResponseSplaGenerate | null;
      if (body?.status === 'success' && body.data?.report) {
        const { report } = body.data;
        const rows = body.data.rows ?? report.row_count;
        const clients = body.data.clients ?? 0;
        const unpriced = body.data.unpriced ?? 0;
        addNotification({
          title: `Snapshot generated — ${formatMonthYear(report.month)}`,
          srTitle: 'Success alert',
          description: `${rows} row${rows === 1 ? '' : 's'} across ${clients} client${clients === 1 ? '' : 's'} (generation ${report.generation}).`,
          variant: 'success',
        });
        if (unpriced > 0) {
          addNotification({
            title: 'Some rows have no sale price',
            srTitle: 'Warning alert',
            description: `${unpriced} row${unpriced === 1 ? '' : 's'} resolved no sale price — the workbook is unaffected (it prints distributor prices), but invoicing will fail loud on them.`,
            variant: 'warning',
          });
        }
        setDrawerOpen(true);
        onGenerated(report);
        onClose();
        return;
      }

      // The frozen/future refusal arrives as a field error on `month` — surface it verbatim.
      const monthError = typeof body?.data?.month === 'string' ? body.data.month : null;
      setFieldError(monthError);
      addNotification({
        title: 'Could not generate the snapshot',
        srTitle: 'Error alert',
        description: monthError ?? body?.message ?? 'Could not generate the snapshot. Please try again.',
        variant: 'danger',
      });
      setDrawerOpen(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setFieldError(message);
      addNotification({
        title: 'Could not generate the snapshot',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Modal variant="small" isOpen={isOpen} onClose={onClose} aria-label="Generate a monthly snapshot">
      <ModalHeader title="Generate snapshot" />
      <ModalBody>
        <Form
          id="spla-generate-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (canSubmit) {
              void handleSubmit();
            }
          }}
        >
          <Alert variant="warning" isInline title="Snapshots read current consumption">
            A snapshot always reads CURRENT consumption — generating a past month late stamps
            today&apos;s counts on it. Regenerating an open month replaces its rows.
          </Alert>
          <FormGroup label="Month" isRequired fieldId="spla-generate-month">
            <TextInput
              id="spla-generate-month"
              type="text"
              inputMode="numeric"
              value={month}
              placeholder="2026-08"
              onChange={(_event, value) => {
                setMonth(value);
                setFieldError(null);
              }}
              validated={helperError !== null ? 'error' : 'default'}
              isDisabled={isGenerating}
              aria-label="Month to snapshot (YYYY-MM)"
            />
            <FormHelperText>
              <HelperText>
                <HelperTextItem variant={helperError !== null ? 'error' : 'default'}>
                  {helperError ??
                    (isRegenerate
                      ? `${formatMonthYear(existing.month)} already has an open snapshot (generation ${existing.generation}) — this replaces its rows.`
                      : 'The reporting month, as YYYY-MM. Future months are refused.')}
                </HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="primary"
          type="submit"
          form="spla-generate-form"
          isDisabled={!canSubmit}
          isLoading={isGenerating}
        >
          {isRegenerate ? 'Regenerate' : 'Generate snapshot'}
        </Button>
        <Button variant="link" onClick={onClose} isDisabled={isGenerating}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default SplaGenerateReportModal;
